<div class="w-full flex flex-col pt-5 gap-md">
  <div class="flex items-center gap-lg">
    <span class="text-4xl font-bold">
      {{ "MasterData.Users.Title" | i18nextEager }}
    </span>
    @if ((divisions.value()?.data ?? []).length > 1) {
      <doka-select
        class="w-48"
        [value]="effectiveDivisionId()"
        (valueChange)="onDivisionChange($any($event))"
        data-testid="users-division-select"
      >
        @for (division of divisions.value()?.data ?? []; track division.id) {
          <doka-select-option [value]="division.id" [label]="division.name"></doka-select-option>
        }
      </doka-select>
    }
    <doka-button
      class="ml-auto"
      color="secondary"
      size="small"
      [disabled]="isSyncingDistributionLists()"
      [title]="'MasterData.Users.SyncDistributionListsHint' | i18nextEager"
      (click)="syncDistributionLists()"
      data-testid="users-sync-distribution-lists-button"
      >{{ "MasterData.Users.SyncDistributionLists" | i18nextEager }}</doka-button
    >
  </div>
  <doka-search-field
    (valueChange)="searchText.set($event)"
    [placeholder]="'Common.Search' | i18nextEager"
    data-testid="users-search-field"
  />

  @if (hierarchy.isLoading()) {
    <app-spinner />
  } @else if (hierarchy.error()) {
    <div class="flex items-center gap-sm text-text-error py-4">
      <doka-icon name="error" />
      <span>{{ "MasterData.Users.LoadError" | i18nextEager }}</span>
    </div>
  } @else {
    <ul class="flex flex-col gap-sm">
      <!-- Division-level users -->
      @if (divisionUsers().length > 0 || effectiveDivisionId()) {
        <li>
          <div class="flex items-center gap-base rounded-md px-2 py-1 bg-background-gray-ultra-light">
            <doka-icon name="domain" class="text-text-light" />
            <span class="font-bold" data-testid="users-division-name">{{ divisionName() }}</span>
            <a
              class="action-icon ml-auto"
              [title]="'MasterData.Users.AddUser' | i18nextEager"
              (click)="openAssignModal('division', effectiveDivisionId()!, divisionName(), getUserIds(divisionUsers()))"
              data-testid="users-division-add-button"
              role="button"
            >
              <doka-icon name="person_add" />
            </a>
          </div>
          <!-- Division Users -->
          @if (divisionUsers().length > 0) {
            <div class="pl-5">
              <div
                class="flex items-center gap-base cursor-pointer rounded-md px-2 py-1 hover:bg-background-gray-ultra-light"
                (click)="toggleUserGroup('division', effectiveDivisionId()!)"
                data-testid="users-division-users-toggle"
              >
                <doka-icon
                  [name]="
                    isUserGroupExpanded('division', effectiveDivisionId()!, divisionUsers())
                      ? 'keyboard_arrow_down'
                      : 'keyboard_arrow_right'
                  "
                />
                <doka-icon name="group" class="text-text-light" />
                <span class="text-sm"
                  >{{ "MasterData.Users.DivisionUsers" | i18nextEager }} ({{ divisionUsers().length }})</span
                >
              </div>
              @if (isUserGroupExpanded("division", effectiveDivisionId()!, divisionUsers())) {
                @for (user of divisionUsers(); track user.id) {
                  <div class="flex items-center gap-base pl-12 py-1 group">
                    <doka-icon name="person" class="text-text-light" />
                    <span class="text-sm" [attr.data-testid]="'users-division-user-' + user.id">{{
                      formatUserName(user)
                    }}</span>
                    <a
                      class="action-icon opacity-0 group-hover:opacity-100 transition-opacity"
                      [title]="'MasterData.Users.RemoveUser' | i18nextEager"
                      (click)="removeUser('division', effectiveDivisionId()!, user)"
                      [attr.data-testid]="'users-division-remove-button-' + user.id"
                      role="button"
                    >
                      <doka-icon name="close" />
                    </a>
                  </div>
                }
              }
            </div>
          }
        </li>
      }

      @for (region of filteredRegions(); track region.id) {
        <li>
          <!-- Region -->
          <div
            class="flex items-center gap-base cursor-pointer rounded-md px-2 py-1 hover:bg-background-gray-ultra-light"
            (click)="toggleRegion(region.id)"
            [attr.data-testid]="'users-region-' + region.id"
          >
            <doka-icon [name]="isRegionExpanded(region.id) ? 'keyboard_arrow_down' : 'keyboard_arrow_right'" />
            <doka-icon name="globe" class="text-text-light" />
            <span class="font-bold">{{ region.name }}</span>
            @if (region.users.length > 0) {
              <span class="text-sm text-text-light">
                ({{ region.users.length }} {{ "MasterData.Users.RegionalDirectors" | i18nextEager }})
              </span>
            }
            <a
              class="action-icon ml-auto"
              [title]="'MasterData.Users.AddUser' | i18nextEager"
              (click)="
                $event.stopPropagation(); openAssignModal('region', region.id, region.name, getUserIds(region.users))
              "
              [attr.data-testid]="'users-region-add-button-' + region.id"
              role="button"
            >
              <doka-icon name="person_add" />
            </a>
          </div>

          @if (isRegionExpanded(region.id)) {
            <!-- Region Users -->
            @if (region.users.length > 0) {
              <div class="pl-5">
                <div
                  class="flex items-center gap-base cursor-pointer rounded-md px-2 py-1 hover:bg-background-gray-ultra-light"
                  (click)="toggleUserGroup('region', region.id)"
                  [attr.data-testid]="'users-region-users-toggle-' + region.id"
                >
                  <doka-icon
                    [name]="
                      isUserGroupExpanded('region', region.id, region.users)
                        ? 'keyboard_arrow_down'
                        : 'keyboard_arrow_right'
                    "
                  />
                  <doka-icon name="group" class="text-text-light" />
                  <span class="text-sm"
                    >{{ "MasterData.Users.RegionalDirectors" | i18nextEager }} ({{ region.users.length }})</span
                  >
                </div>
                @if (isUserGroupExpanded("region", region.id, region.users)) {
                  @for (user of region.users; track user.id) {
                    <div class="flex items-center gap-base pl-12 py-1 group">
                      <doka-icon name="person" class="text-text-light" />
                      <span class="text-sm" [attr.data-testid]="'users-region-user-' + region.id + '-' + user.id">{{
                        formatUserName(user)
                      }}</span>
                      <a
                        class="action-icon opacity-0 group-hover:opacity-100 transition-opacity"
                        [title]="'MasterData.Users.RemoveUser' | i18nextEager"
                        (click)="removeUser('region', region.id, user)"
                        [attr.data-testid]="'users-region-remove-button-' + region.id + '-' + user.id"
                        role="button"
                      >
                        <doka-icon name="close" />
                      </a>
                    </div>
                  }
                }
              </div>
            }

            <!-- Countries -->
            <ul class="pl-5">
              @for (country of region.countries; track country.id) {
                <li>
                  <div
                    class="flex items-center gap-base cursor-pointer rounded-md px-2 py-1 hover:bg-background-gray-ultra-light"
                    (click)="toggleCountry(country.id)"
                    [attr.data-testid]="'users-country-' + country.id"
                  >
                    <doka-icon
                      [name]="isCountryExpanded(country.id) ? 'keyboard_arrow_down' : 'keyboard_arrow_right'"
                    />
                    <doka-icon name="map" class="text-text-light" />
                    <span class="font-bold">{{ country.name }}</span>
                    @if (country.users.length > 0) {
                      <span class="text-sm text-text-light">
                        ({{ country.users.length }} {{ "MasterData.Users.HeadsOfCountry" | i18nextEager }})
                      </span>
                    }
                    <a
                      class="action-icon ml-auto"
                      [title]="'MasterData.Users.AddUser' | i18nextEager"
                      (click)="
                        $event.stopPropagation();
                        openAssignModal('country', country.id, country.name, getUserIds(country.users))
                      "
                      [attr.data-testid]="'users-country-add-button-' + country.id"
                      role="button"
                    >
                      <doka-icon name="person_add" />
                    </a>
                  </div>

                  @if (isCountryExpanded(country.id)) {
                    <!-- Country Users -->
                    @if (country.users.length > 0) {
                      <div class="pl-5">
                        <div
                          class="flex items-center gap-base cursor-pointer rounded-md px-2 py-1 hover:bg-background-gray-ultra-light"
                          (click)="toggleUserGroup('country', country.id)"
                          [attr.data-testid]="'users-country-users-toggle-' + country.id"
                        >
                          <doka-icon
                            [name]="
                              isUserGroupExpanded('country', country.id, country.users)
                                ? 'keyboard_arrow_down'
                                : 'keyboard_arrow_right'
                            "
                          />
                          <doka-icon name="group" class="text-text-light" />
                          <span class="text-sm"
                            >{{ "MasterData.Users.HeadsOfCountry" | i18nextEager }} ({{ country.users.length }})</span
                          >
                        </div>
                        @if (isUserGroupExpanded("country", country.id, country.users)) {
                          @for (user of country.users; track user.id) {
                            <div class="flex items-center gap-base pl-12 py-1 group">
                              <doka-icon name="person" class="text-text-light" />
                              <span
                                class="text-sm"
                                [attr.data-testid]="'users-country-user-' + country.id + '-' + user.id"
                                >{{ formatUserName(user) }}</span
                              >
                              <a
                                class="action-icon opacity-0 group-hover:opacity-100 transition-opacity"
                                [title]="'MasterData.Users.RemoveUser' | i18nextEager"
                                (click)="removeUser('country', country.id, user)"
                                [attr.data-testid]="'users-country-remove-button-' + country.id + '-' + user.id"
                                role="button"
                              >
                                <doka-icon name="close" />
                              </a>
                            </div>
                          }
                        }
                      </div>
                    }

                    <!-- Branches -->
                    <ul class="pl-5">
                      @for (branch of country.branches; track branch.id) {
                        <li>
                          <div
                            class="flex items-center gap-base cursor-pointer rounded-md px-2 py-1 hover:bg-background-gray-ultra-light"
                            (click)="toggleBranch(branch.id)"
                            [attr.data-testid]="'users-branch-' + branch.id"
                          >
                            <doka-icon
                              [name]="isBranchExpanded(branch.id) ? 'keyboard_arrow_down' : 'keyboard_arrow_right'"
                            />
                            <doka-icon name="location_on" class="text-text-light" />
                            <span class="font-bold">{{ branch.name }}</span>
                            @if (branch.users.length > 0) {
                              <span class="text-sm text-text-light">
                                ({{ branch.users.length }} {{ "MasterData.Users.BranchWorkers" | i18nextEager }})
                              </span>
                            }
                            <a
                              class="action-icon ml-auto"
                              [title]="'MasterData.Users.AddUser' | i18nextEager"
                              (click)="
                                $event.stopPropagation();
                                openAssignModal('branch', branch.id, branch.name, getUserIds(branch.users))
                              "
                              [attr.data-testid]="'users-branch-add-button-' + branch.id"
                              role="button"
                            >
                              <doka-icon name="person_add" />
                            </a>
                          </div>

                          @if (isBranchExpanded(branch.id)) {
                            @for (user of branch.users; track user.id) {
                              <div class="flex items-center gap-base pl-12 py-1 group">
                                <doka-icon name="person" class="text-text-light" />
                                <span
                                  class="text-sm"
                                  [attr.data-testid]="'users-branch-user-' + branch.id + '-' + user.id"
                                  >{{ formatUserName(user) }}</span
                                >
                                <a
                                  class="action-icon opacity-0 group-hover:opacity-100 transition-opacity"
                                  [title]="'MasterData.Users.RemoveUser' | i18nextEager"
                                  (click)="removeUser('branch', branch.id, user)"
                                  [attr.data-testid]="'users-branch-remove-button-' + branch.id + '-' + user.id"
                                  role="button"
                                >
                                  <doka-icon name="close" />
                                </a>
                              </div>
                            }
                            @if (branch.users.length === 0) {
                              <div
                                class="pl-12 py-1 text-sm text-text-light italic"
                                [attr.data-testid]="'users-branch-no-users-' + branch.id"
                              >
                                {{ "MasterData.Users.NoUsers" | i18nextEager }}
                              </div>
                            }
                          }
                        </li>
                      }
                    </ul>
                  }
                </li>
              }
            </ul>
          }
        </li>
      }
    </ul>
  }
</div>
